import React, { useEffect, useRef, useState } from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { Tag } from '@/design-system/tag';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import { CsvErrorTable, CsvFileProblem } from '../../components/CsvErrorTable';
import {
  describeUploadError,
  localFileProblem,
  messagesToErrors,
  type PickError,
} from '../../components/csvImportErrors';
import { DisabledReason } from '../../components/dialogs';
import {
  codeCls,
  metaCls,
  nameCls,
  numCls,
  StockTypeBadge,
  SumTile,
} from '../../components/ui';
import {
  downloadCsv,
  fmtQty,
  norm,
  parseCsvRows,
  readCsvFile,
} from '../../format';
import type {
  ClientRef,
  OpnameImportLine,
  OpnameImportResult,
} from '../../types';
import { fmtGap, gapCls } from './opnameDraft';

/** Columns of the count sheet, in template order. */
export const OPNAME_CSV_HEADERS = [
  'sku',
  'storage_name',
  'batch_number',
  'qty_physical',
  'notes',
  'stock_type',
];
/** CSV column → label shown next to the raw column name in the error table. */
export const OPNAME_CSV_COLUMN_LABEL: Readonly<Record<string, string>> = {
  row: 'Seluruh baris',
  sku: 'SKU',
  storage_name: 'Nama storage',
  batch_number: 'Nomor batch',
  qty_physical: 'Qty fisik',
  notes: 'Catatan',
  expired_date: 'Tanggal kedaluwarsa',
  stock_type: 'Tipe stok',
};

const columnLabel = (field: string): string | null =>
  OPNAME_CSV_COLUMN_LABEL[field] ?? null;

const FILE_HINT =
  'Download template, salin data ke sana (nama kolom di baris pertama harus sama persis), lalu pilih ulang file.';

const REQUIRED = ['sku', 'storage_name', 'batch_number', 'qty_physical'];
const MAX_BYTES = 2 * 1024 * 1024;
/** Rows one sheet may carry. */
export const OPNAME_IMPORT_MAX_ROWS = 500;

const COLUMNS: TableDataColumn<OpnameImportLine>[] = [
  {
    key: 'lineIndex',
    title: 'Baris',
    render: (_v, l) => <span className="tabular-nums">{l.lineIndex + 1}</span>,
  },
  {
    key: 'storage',
    title: 'Storage',
    render: (_v, l) => <span className={nameCls}>{l.storageName}</span>,
  },
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => (
      <div className="min-w-0">
        <p className={codeCls}>{l.sku}</p>
        <p className={metaCls}>{l.itemName}</p>
      </div>
    ),
  },
  {
    key: 'batchNumber',
    title: 'Batch',
    render: (_v, l) =>
      l.batchNumber ? (
        <span className={numCls}>{l.batchNumber}</span>
      ) : (
        <Tag size="xs" variant="stroke">
          Batch baru
        </Tag>
      ),
  },
  {
    key: 'stockType',
    title: 'Tipe',
    render: (_v, l) => <StockTypeBadge type={l.stockType} />,
  },
  {
    key: 'qtySystem',
    title: 'Sistem',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtySystem)}</span>,
  },
  {
    key: 'qtyPhysical',
    title: 'Fisik',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyPhysical)}</span>,
  },
  {
    key: 'qtyGap',
    title: 'Selisih',
    align: 'right',
    render: (_v, l) => (
      <span
        className={`font-mono text-[12px] font-semibold tabular-nums ${gapCls(l.qtyGap)}`}
      >
        {fmtGap(l.qtyGap)}
      </span>
    ),
  },
];

/**
 * The first row's problem as a message, or null when it is a count sheet:
 * every required column is named (case, spaces and underscores ignored).
 */
const headerProblem = (rows: string[][]): string | null => {
  const header = new Set((rows[0] ?? []).map(norm));
  const missing = REQUIRED.filter((c) => !header.has(norm(c)));
  return missing.length
    ? `Kolom wajib tidak ditemukan: ${missing.join(', ')}. Unduh template untuk format yang benar.`
    : null;
};

/**
 * Count sheet import: pick a CSV (sku, storage_name, batch_number,
 * qty_physical, notes, optional stock_type) → the sheet is checked here
 * (header, size, row cap) → the server resolves it against the stock as a dry
 * run (nothing is saved) → the resolved rows replace the table. A sheet with
 * errors can not be applied: the messages are listed so the file can be fixed
 * and picked again.
 */
export const OpnameCsvImportModal: React.FC<{
  open: boolean;
  hubID: number;
  client: ClientRef | null;
  /** The table already holds rows: they are replaced. */
  replaces: boolean;
  onClose: () => void;
  onApply: (lines: OpnameImportLine[]) => void;
}> = ({ open, hubID, client, replaces, onClose, onApply }) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState<OpnameImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PickError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setFileName(null);
    setResult(null);
    setBusy(false);
    setError(null);
  }, [open]);

  if (!open) return null;

  const pick = async (f: File | null) => {
    if (!f || !client) return;
    setError(null);
    setResult(null);
    setFileName(null);
    const read = await readCsvFile(f, MAX_BYTES);
    if (read.error !== null) {
      setError(
        localFileProblem(
          read.error,
          'Pakai file .csv dari "Download template", maksimal 2 MB.'
        )
      );
      return;
    }
    const rows = parseCsvRows(read.text);
    const problem = headerProblem(rows);
    if (problem) {
      setError(localFileProblem(problem, FILE_HINT));
      return;
    }
    if (rows.length < 2) {
      setError(
        localFileProblem(
          'File tidak punya baris data.',
          'Isi minimal satu baris hitungan di bawah header, lalu pilih ulang file.'
        )
      );
      return;
    }
    if (rows.length - 1 > OPNAME_IMPORT_MAX_ROWS) {
      setError(
        localFileProblem(
          `Maksimal ${OPNAME_IMPORT_MAX_ROWS} baris per import — file ini ${rows.length - 1} baris.`,
          'Pecah jadi beberapa file, lalu import satu per satu.'
        )
      );
      return;
    }
    setFileName(f.name);
    setBusy(true);
    try {
      setResult(
        await warehouseApi.importStockOpname({ hubID, clientID: client.id }, f)
      );
    } catch (err) {
      setError(
        describeUploadError(parseWarehouseError(err), {
          fileHint: FILE_HINT,
          requestTitle: 'Pemeriksaan file gagal',
          requestAction: 'Pilih file CSV',
        })
      );
    } finally {
      setBusy(false);
    }
  };

  // Structured errors; an older server only sends the "Baris N: …" strings.
  const rowErrors = !result
    ? []
    : (result.errors ??
      (result.totalFail > 0 ? messagesToErrors(result.messages) : []));
  const blocked = !client
    ? 'Pilih client dulu'
    : busy
      ? 'Memeriksa file…'
      : !result
        ? 'Pilih file CSV'
        : result.totalFail > 0
          ? `${result.totalFail} baris error — perbaiki file lalu pilih ulang`
          : result.lines.length === 0
            ? 'Tidak ada baris yang valid'
            : null;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon="ri-upload-2-line"
      title="Import hitung fisik (CSV)"
      subtitle={client?.name}
      width="xl"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="flex items-center justify-end gap-2">
          {blocked && (
            <DisabledReason testId="opname-import-reason" className="mr-auto">
              {blocked}
            </DisabledReason>
          )}
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={busy}
          >
            Batal
          </Button>
          <Button
            disabled={!!blocked}
            data-testid="opname-import-apply"
            onClick={() => result && onApply(result.lines)}
          >
            {result && !blocked
              ? `Gunakan ${result.lines.length} baris`
              : 'Gunakan baris'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-sub-600">
          Kolom: sku, storage_name, batch_number, qty_physical, notes,
          stock_type (opsional). Kosongkan batch_number untuk batch yang belum
          tercatat di sistem. stock_type diisi REGULAR atau RETURN: tipe batch
          baru (kosong = REGULAR), atau baris stok yang dihitung bila batch yang
          sama ada sebagai REGULAR dan RETURN di storage itu.
          {replaces && ' Baris yang sudah ada di tabel akan diganti.'}
        </p>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="opname-import-file"
            aria-label="File CSV hitung fisik"
            onChange={(e) => {
              void pick(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Button
            tone="neutral"
            style="stroke"
            loading={busy}
            disabled={!client}
            onClick={() => inputRef.current?.click()}
            leftIcon={<i className="ri-file-upload-line text-[14px]" />}
          >
            {fileName ? 'Ganti file' : 'Pilih file CSV'}
          </Button>
          {fileName && <span className={metaCls}>{fileName}</span>}
          <span className="flex-1" />
          <Button
            tone="primary"
            style="link"
            size="xs"
            className="text-xs font-semibold"
            data-testid="opname-import-template"
            onClick={() =>
              downloadCsv('template-stock-opname.csv', [OPNAME_CSV_HEADERS])
            }
            leftIcon={
              <i className="ri-download-2-line text-[14px]" aria-hidden />
            }
          >
            Download template
          </Button>
        </div>
        {error && (
          <CsvFileProblem problem={error} testId="opname-import-error" />
        )}
        {result && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <SumTile
                label="Total baris"
                value={result.totalData}
                tone="neutral"
                testId="opname-import-total"
              />
              <SumTile
                label="Valid"
                value={result.totalSuccess}
                tone="success"
                testId="opname-import-success"
              />
              <SumTile
                label="Error"
                value={result.totalFail}
                tone={result.totalFail ? 'error' : 'neutral'}
                testId="opname-import-fail"
              />
            </div>
            {rowErrors.length > 0 && (
              <div data-testid="opname-import-messages">
                <CsvErrorTable
                  errors={rowErrors}
                  columnLabel={columnLabel}
                  fileName={fileName}
                />
              </div>
            )}
            {rowErrors.length === 0 && result.messages.length > 0 && (
              <Alert
                status="error"
                appearance="stroke"
                size="lg"
                title="Baris yang perlu diperbaiki"
                data-testid="opname-import-messages"
              >
                <ul className="list-disc pl-4">
                  {result.messages.map((m, i) => (
                    <li key={`${i}-${m}`}>{m}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {result.lines.length > 0 && (
              <div className="max-h-[320px] overflow-auto">
                <TableData size="sm" data={result.lines} columns={COLUMNS} />
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
